import { useCallback } from "react";
import { useSelector } from "react-redux";

import {
  InvitationActionSourceType,
  useBandContext,
  useBandInvitationContext,
  usePermissionsContext,
} from "Bands/contexts";
import { sentBandInvitationsSelector } from "Bands/state";
import { BAND_ACTIONS } from "Bands/types";
import {
  getSentInvitationsActions,
  useBandRoute,
  useGetMemberTableColumns,
  usePagination,
} from "Bands/utils";
import { copy } from "shared/assets";
import { Clock } from "shared/assets/icons";
import { Card, IActionDropdownItem, Pager, Table } from "shared/components";
import { Spacer } from "shared/layout";
import { BAND_PATHS } from "Bands/router";
import { API_ENDPOINTS } from "shared/constants";
import { getIdFromPath } from "shared/utils";

const {
  members: { sentInvitationsTable: tableCopy },
} = copy;

export const SentInvitationsTable = () => {
  const { allBandUsers } = useBandContext();
  const { userCanManageMembers } = usePermissionsContext();

  const invitations = useSelector(sentBandInvitationsSelector);
  const pagination = usePagination(BAND_ACTIONS.getSentBandInvitations);
  const {
    openSendModal,
    openRevokeModal,
    openDeleteModal,
    fetchSentInvitations,
    setInvitationActionSource,
  } = useBandInvitationContext();
  const { pushRoute } = useBandRoute();

  const getRowActions = useCallback(
    (invitationId: string): IActionDropdownItem[] => {
      const invitation = invitations.find(({ id }) => invitationId === id);

      if (!invitation) return [];

      if (!userCanManageMembers) return [];

      const bandUser = allBandUsers.find(
        ({ invitation: inv }) => inv && inv.id === API_ENDPOINTS.BAND_INVITATION(invitationId)
      );

      if (!bandUser) return [];

      const actions = getSentInvitationsActions({
        handleResend: () => openSendModal(invitation, InvitationActionSourceType.sent),
        handleRevoke: () => openRevokeModal(invitation),
        handleEdit: () => {
          setInvitationActionSource(InvitationActionSourceType.sent);

          pushRoute(BAND_PATHS.EDIT_INVITATION, {
            params: { invitationId },
          });
        },
        handleEditPermissions: () =>
          pushRoute(BAND_PATHS.EDIT_PERMISSION, {
            params: {
              userId: getIdFromPath(bandUser.id, API_ENDPOINTS.BAND_USERS) || "",
            },
          }),
        handleDelete: () => openDeleteModal(invitation, InvitationActionSourceType.sent),
      });

      return actions;
    },
    [
      invitations,
      userCanManageMembers,
      allBandUsers,
      openSendModal,
      openRevokeModal,
      setInvitationActionSource,
      pushRoute,
      openDeleteModal,
    ]
  );

  const { columns } = useGetMemberTableColumns(invitations[0]);

  if (!invitations.length) return null;

  return (
    <>
      <Card title={tableCopy.title} icon={Clock}>
        <Spacer size="s">
          <Table
            data={invitations}
            columns={columns}
            getRowActions={getRowActions}
            dataTestId="sent_invitations_table"
            options={{
              showActions: userCanManageMembers,
            }}
          />
          <Pager {...pagination} onPageChange={fetchSentInvitations} />
        </Spacer>
      </Card>
    </>
  );
};
